import { action, makeObservable, observable } from 'mobx';
import { useEffect } from 'react';
import { API } from '~/api';
import { GridStateModel } from '~/model/model.gridstate';
import { BrandId } from '~/shared/dto/id';
import { Singleton } from '~/shared/service/service.base';
import { Async } from '~/shared/tools/async';
import { ApiService } from './service.api';

@Singleton()
export class PromotionService {
	readonly items = observable.array([] as API.PromotionItem[], { deep: false });
	readonly async = new Async();
	readonly gridState = new GridStateModel('promotions');

	@observable totalCount = 0;

	private brandId?: BrandId = undefined;

	readonly brands = observable.array([] as API.BrandName[], { deep: false });
	readonly brandsAsync = new Async();

	readonly fragrances = observable.array([] as API.FragrancePromo[], { deep: false });
	readonly fragrancesAsync = new Async();

	constructor(private api: ApiService) {
		makeObservable(this);
	}

	loacdAndSubscribe = () => {
		this.reload();
		return this.gridState.subscribe(this.reload);
	};

	reload = () => {
		this.async.call(async () => {
			const { data } = await this.api.promotionGetAll({
				limit: this.gridState.pageSize,
				offset: this.gridState.offset,
				filter: this.gridState.filterQuery,
				sort: this.gridState.sort,
				sortDir: this.gridState.sortDir,
			});
			return () => {
				this.items.replace(data.items);
				this.totalCount = data.total;
			};
		});
	};

	async getOne(id: number | undefined) {
		if (id === undefined) return;
		return this.async.exec(() => this.api.promotionGetOne(id).then(resp => resp.data));
	}

	async save(id: number | undefined, data: API.PromotionCreateDto) {
		return this.async.exec(async () => {
			data.logo = await this.api.uploadImg(data.logo);
			data.logoDark = await this.api.uploadImg(data.logoDark);
			data.bannerImage = await this.api.uploadImg(data.bannerImage);
			if (id === undefined) await this.api.promotionCreate(data);
			else await this.api.promotionUpdate(id, data);
		});
	}

	delete = async (id: number) => {
		return this.async.exec(() => this.api.promotionDelete(id)).then(this.reload);
	};

	@action.bound setBrandId(brandId: BrandId) {
		this.brandId = brandId;
	}

	useBrands = () => {
		useEffect(() => {
			this.fetchBrands();
		}, []);
	};

	fetchBrands = () => {
		this.brandsAsync.call(async () => {
			const { data } = await this.api.brandGetNames();
			return () => {
				this.brands.replace(data.items);
			};
		});
	};

	@action.bound setFragrances(fragrances: API.FragrancePromo[]) {
		this.fragrances.replace(fragrances);
	}

	useFragrances = (brandId: BrandId | undefined) => {
		useEffect(() => {
			if (!brandId) {
				this.setFragrances([]);
				return;
			}
			this.setBrandId(brandId);
			this.fetchFragrances();
		}, [brandId]);
	};

	private fetchFragrances = () => {
		this.fragrancesAsync.call(async () => {
			if (this.brandId) {
				const { data } = await this.api.fragranceGetPromoList(this.brandId);
				return () => {
					this.fragrances.replace(data.items.sort((a, b) => a.name.localeCompare(b.name)));
				};
			}
		});
	};
}
